import { TextField } from "@mui/material";
import * as React from "react";

import { EditFieldButton } from "@/shared/components";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";

import styles from "./EditableCollectionMetadata.module.css";

interface EditableCollectionMetadataProps {
  name: string | undefined;
  description: string | undefined;
  collectionId: string;
  orgId: string;
}

export const EditableCollectionMetadata: React.FC<
  EditableCollectionMetadataProps
> = ({ name, description, collectionId, orgId }) => {
  const [showEditableIcon, setShowEditableIcon] = React.useState(false);
  const [editable, setEditable] = React.useState(false);
  const [currentName, setCurrentName] = React.useState<string | undefined>(
    name,
  );
  const [currentDescription, setCurrentDescription] = React.useState<
    string | undefined
  >(description);
  const nameFieldRef = React.useRef<HTMLInputElement>(null);
  const descFieldRef = React.useRef<HTMLInputElement>(null);

  const { mutateAsync: updateCollection } = useUpdateCollection();

  const save = async (
    updatedName: string | undefined,
    updatedDesc: string | undefined,
  ): Promise<boolean> => {
    try {
      await updateCollection({
        collectionId,
        resourceOwnerId: orgId,
        updates: {
          name: updatedName || undefined,
          description: updatedDesc || undefined,
        },
      });
      return true;
    } catch {
      return false;
    }
  };

  const onClick = async () => {
    if (editable) {
      const updatedName = nameFieldRef.current?.value;
      const updatedDesc = descFieldRef.current?.value;
      const ok = await save(updatedName, updatedDesc);
      if (ok) {
        setCurrentName(updatedName);
        setCurrentDescription(updatedDesc);
        setEditable(false);
      }
    } else {
      setEditable(true);
    }
  };

  return (
    <div
      className={styles.wrapper}
      onMouseOver={() => setShowEditableIcon(true)}
      onMouseLeave={() => setShowEditableIcon(false)}
    >
      <div className={styles.nameRow}>
        {editable ? (
          <TextField
            style={{
              marginTop: "var(--spacing-xs)",
              marginBottom: "var(--spacing-xs)",
              width: 300,
            }}
            autoFocus
            size="small"
            defaultValue={currentName}
            variant="outlined"
            placeholder="Collection Name"
            inputProps={{ ref: nameFieldRef, style: { fontSize: "0.875rem" } }}
          />
        ) : (
          <span className={styles.name}>{currentName || <i>No Name</i>}</span>
        )}
        <EditFieldButton
          editable={editable}
          showEditableIcon={showEditableIcon}
          onClick={() => void onClick()}
        />
      </div>

      {editable ? (
        <TextField
          style={{ marginTop: "var(--spacing-xs)", width: "100%" }}
          multiline
          minRows={2}
          maxRows={10}
          size="small"
          placeholder="Collection Description"
          defaultValue={currentDescription}
          variant="outlined"
          inputProps={{ ref: descFieldRef, style: { fontSize: "0.875rem" } }}
        />
      ) : (
        <div className={styles.description}>
          {currentDescription ? (
            <RobotoMarkdown content={currentDescription} />
          ) : (
            <i>No Description</i>
          )}
        </div>
      )}
    </div>
  );
};
